import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Icons
import {
  User,
  Image,
  Mic,
  Video,
  Wand2,
  Loader2,
  Download,
  Play,
  Pause,
  RefreshCw,
  CheckCircle2,
  Volume2,
  FileText
} from "lucide-react";

const MediaGenerator = () => {
  const [profile, setProfile] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Portrait generation
  const [generatingPortrait, setGeneratingPortrait] = useState(false);
  const [portraitStyle, setPortraitStyle] = useState("realistic");
  const [customPrompt, setCustomPrompt] = useState("");
  const [generatedPortrait, setGeneratedPortrait] = useState(null);
  
  // Voice generation
  const [generatingVoice, setGeneratingVoice] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("onyx");
  const [selectedScriptId, setSelectedScriptId] = useState("");
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Video generation
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoDuration, setVideoDuration] = useState(4);
  const [selectedVideoScriptId, setSelectedVideoScriptId] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profileRes, scriptsRes] = await Promise.all([
        api.get('/abraham/profile'),
        api.get('/videos?limit=365')
      ]);
      
      setProfile(profileRes.data);
      setScripts(scriptsRes.data);
      
      if (scriptsRes.data.length > 0) {
        setSelectedScriptId(scriptsRes.data[0].id);
        setSelectedVideoScriptId(scriptsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Portrait Generation
  const generatePortrait = async () => {
    try {
      setGeneratingPortrait(true);
      const response = await api.post('/abraham/generate-portrait', {
        style: portraitStyle,
        custom_prompt: customPrompt || null
      });
      
      setGeneratedPortrait(response.data.image_base64);
      toast.success('Portrait generated successfully!');
      
      // Refresh profile to get updated portrait URL
      const profileRes = await api.get('/abraham/profile');
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Failed to generate portrait:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate portrait');
    } finally {
      setGeneratingPortrait(false);
    }
  };

  // Voice Generation
  const generateVoice = async () => {
    if (!selectedScriptId) {
      toast.error('Please select a script first');
      return;
    }
    
    try {
      setGeneratingVoice(true);
      const response = await api.post('/videos/generate-voice', {
        video_id: selectedScriptId,
        voice: selectedVoice
      });
      
      setGeneratedAudio(response.data.audio_base64);
      toast.success('Voice generated successfully!');
      
      // Auto-play the generated audio
      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
      setAudioPlayer(audio);
      audio.play();
      setIsPlaying(true);
      
      audio.onended = () => setIsPlaying(false);
    } catch (error) {
      console.error('Failed to generate voice:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate voice');
    } finally {
      setGeneratingVoice(false);
    }
  };

  const toggleAudio = () => {
    if (!audioPlayer) return;
    
    if (isPlaying) {
      audioPlayer.pause();
      setIsPlaying(false);
    } else {
      audioPlayer.play();
      setIsPlaying(true);
    }
  };

  // Video Generation
  const generateVideo = async () => {
    if (!selectedVideoScriptId) {
      toast.error('Please select a script first');
      return;
    }
    
    try {
      setGeneratingVideo(true);
      const response = await api.post('/videos/generate-video', {
        video_id: selectedVideoScriptId,
        duration: videoDuration
      });
      
      toast.success('Video generation started! This may take several minutes.');
    } catch (error) {
      console.error('Failed to start video generation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start video generation');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const voices = [
    { value: "onyx", label: "Onyx", description: "Deep, authoritative" },
    { value: "alloy", label: "Alloy", description: "Neutral, balanced" },
    { value: "echo", label: "Echo", description: "Smooth, calm" },
    { value: "fable", label: "Fable", description: "Expressive, storytelling" },
    { value: "nova", label: "Nova", description: "Energetic, upbeat" },
    { value: "sage", label: "Sage", description: "Wise, measured" },
    { value: "shimmer", label: "Shimmer", description: "Bright, cheerful" },
  ];

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="media-generator-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[500px] rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="media-generator">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">
          AI Media Generator
        </h1>
        <p className="text-muted-foreground mt-1">
          Create portraits, voices, and videos for Abraham's journey
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="portrait" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="portrait" className="gap-2" data-testid="tab-portrait">
            <Image className="w-4 h-4" />
            Portrait
          </TabsTrigger>
          <TabsTrigger value="voice" className="gap-2" data-testid="tab-voice">
            <Mic className="w-4 h-4" />
            Voice
          </TabsTrigger>
          <TabsTrigger value="video" className="gap-2" data-testid="tab-video">
            <Video className="w-4 h-4" />
            Video
          </TabsTrigger>
        </TabsList>

        {/* Portrait Tab */}
        <TabsContent value="portrait" className="mt-6" data-testid="portrait-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Controls */}
            <Card className="card-patriarch">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Image className="w-5 h-5 text-accent" />
                  Generate Portrait
                </CardTitle>
                <CardDescription>
                  Create a realistic AI-generated portrait of Abraham using GPT Image 1
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Style</label>
                  <Select value={portraitStyle} onValueChange={setPortraitStyle}>
                    <SelectTrigger data-testid="portrait-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="realistic">Realistic</SelectItem>
                      <SelectItem value="artistic">Artistic</SelectItem>
                      <SelectItem value="classical">Classical Painting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Custom Prompt (Optional)
                  </label>
                  <Textarea 
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Add additional details for the portrait..."
                    className="min-h-[100px]"
                    data-testid="custom-prompt-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Leave empty for the default portrait style
                  </p>
                </div>

                <Button 
                  onClick={generatePortrait}
                  disabled={generatingPortrait}
                  className="w-full gap-2"
                  data-testid="generate-portrait-btn"
                >
                  {generatingPortrait ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating Portrait...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Portrait
                    </>
                  )}
                </Button>

                {generatingPortrait && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground text-center">
                      This may take up to a minute...
                    </p>
                    <Progress value={33} className="animate-pulse" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="card-patriarch">
              <CardHeader>
                <CardTitle className="font-display">Portrait Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-square rounded-xl bg-muted flex items-center justify-center overflow-hidden">
                  {generatedPortrait ? (
                    <img 
                      src={`data:image/png;base64,${generatedPortrait}`}
                      alt="Generated Abraham Portrait"
                      className="w-full h-full object-cover"
                      data-testid="generated-portrait"
                    />
                  ) : profile?.portrait_url ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${profile.portrait_url}`}
                      alt="Abraham Portrait"
                      className="w-full h-full object-cover"
                      data-testid="current-portrait"
                    />
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <User className="w-20 h-20 mx-auto mb-4 opacity-50" />
                      <p>No portrait generated yet</p>
                    </div>
                  )}
                </div>
                
                {(generatedPortrait || profile?.portrait_url) && (
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1 gap-2"
                      onClick={() => {
                        const link = document.createElement('a');
                        if (generatedPortrait) {
                          link.href = `data:image/png;base64,${generatedPortrait}`;
                        } else {
                          link.href = `${process.env.REACT_APP_BACKEND_URL}${profile.portrait_url}`;
                        }
                        link.download = 'abraham_portrait.png';
                        link.click();
                      }}
                      data-testid="download-portrait-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button 
                      variant="outline" 
                      className="gap-2"
                      onClick={generatePortrait}
                      disabled={generatingPortrait}
                      data-testid="regenerate-portrait-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Regenerate
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Voice Tab */}
        <TabsContent value="voice" className="mt-6" data-testid="voice-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Controls */}
            <Card className="card-patriarch">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Mic className="w-5 h-5 text-accent" />
                  Generate Voice
                </CardTitle>
                <CardDescription>
                  Create voice narration using OpenAI TTS HD
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Select Script</label>
                  <Select value={selectedScriptId} onValueChange={setSelectedScriptId}>
                    <SelectTrigger data-testid="voice-script-select">
                      <SelectValue placeholder="Choose a script..." />
                    </SelectTrigger>
                    <SelectContent>
                      {scripts.map((script) => (
                        <SelectItem key={script.id} value={script.id}>
                          Day {script.day_number}: {script.title?.substring(0, 30)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Voice</label>
                  <div className="grid grid-cols-2 gap-2">
                    {voices.map((voice) => (
                      <button
                        key={voice.value}
                        onClick={() => setSelectedVoice(voice.value)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          selectedVoice === voice.value
                            ? 'border-accent bg-accent/10'
                            : 'border-border hover:border-accent/50'
                        }`}
                        data-testid={`voice-option-${voice.value}`}
                      >
                        <p className="font-medium text-sm">{voice.label}</p>
                        <p className="text-xs text-muted-foreground">{voice.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={generateVoice}
                  disabled={generatingVoice || !selectedScriptId}
                  className="w-full gap-2"
                  data-testid="generate-voice-btn"
                >
                  {generatingVoice ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating Voice...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Voice
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="card-patriarch">
              <CardHeader>
                <CardTitle className="font-display">Voice Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-video rounded-xl bg-muted flex flex-col items-center justify-center">
                  {generatedAudio ? (
                    <div className="text-center">
                      <div className="w-24 h-24 rounded-full bg-accent/20 flex items-center justify-center mb-4 mx-auto">
                        <Volume2 className="w-12 h-12 text-accent" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">Voice generated successfully</p>
                      <Button 
                        onClick={toggleAudio}
                        className="gap-2"
                        data-testid="play-audio-btn"
                      >
                        {isPlaying ? (
                          <>
                            <Pause className="w-4 h-4" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            Play
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <Mic className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No voice generated yet</p>
                      <p className="text-sm">Select a script and generate voice</p>
                    </div>
                  )}
                </div>

                {generatedAudio && (
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1 gap-2"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `data:audio/mp3;base64,${generatedAudio}`;
                        link.download = 'abraham_voice.mp3';
                        link.click();
                      }}
                      data-testid="download-audio-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download MP3
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Video Tab */}
        <TabsContent value="video" className="mt-6" data-testid="video-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Controls */}
            <Card className="card-patriarch">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Video className="w-5 h-5 text-accent" />
                  Generate Video
                </CardTitle>
                <CardDescription>
                  Create videos using Sora 2 AI video generation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Select Script</label>
                  <Select value={selectedVideoScriptId} onValueChange={setSelectedVideoScriptId}>
                    <SelectTrigger data-testid="video-script-select">
                      <SelectValue placeholder="Choose a script..." />
                    </SelectTrigger>
                    <SelectContent>
                      {scripts.map((script) => (
                        <SelectItem key={script.id} value={script.id}>
                          Day {script.day_number}: {script.title?.substring(0, 30)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Video Duration</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[4, 8, 12].map((duration) => (
                      <button
                        key={duration}
                        onClick={() => setVideoDuration(duration)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          videoDuration === duration
                            ? 'border-accent bg-accent/10'
                            : 'border-border hover:border-accent/50'
                        }`}
                        data-testid={`duration-option-${duration}`}
                      >
                        <p className="font-display font-bold text-lg">{duration}</p>
                        <p className="text-xs text-muted-foreground">seconds</p>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Note: Full 1-3 minute videos require combining multiple clips
                  </p>
                </div>

                <Button 
                  onClick={generateVideo}
                  disabled={generatingVideo || !selectedVideoScriptId}
                  className="w-full gap-2"
                  data-testid="generate-video-btn"
                >
                  {generatingVideo ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting Generation...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Video
                    </>
                  )}
                </Button>

                <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">Note:</strong> Video generation takes 5-10 minutes. 
                    The video will be saved to your script automatically when complete.
                    Check the script status in the Scripts page.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="card-patriarch">
              <CardHeader>
                <CardTitle className="font-display">About Sora 2</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="aspect-video rounded-xl bg-muted flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Videos appear here when generated</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">HD Quality</p>
                      <p className="text-xs text-muted-foreground">1280x720 resolution videos</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">AI-Generated Scenes</p>
                      <p className="text-xs text-muted-foreground">Based on your script content</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Cinematic Style</p>
                      <p className="text-xs text-muted-foreground">Professional lighting and composition</p>
                    </div>
                  </div>
                </div>

                <Link to="/scripts">
                  <Button variant="outline" className="w-full gap-2" data-testid="view-scripts-link">
                    <FileText className="w-4 h-4" />
                    View All Scripts
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MediaGenerator;
